import { FC, useState } from 'react';
import SimpleModal from 'components/SimpleModal';
import DeactivationReasonSelect from 'components/DeactivationReasonSelect';
import { Box, TextField, Tooltip } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { DeactivationReason } from '@constants/deactivation';
import useDeactivateTeams from 'hooks/useDeactivateTeams';

export interface DeactivateTeamsModalProps {
  onClose: VoidFunction;
  onAfterDeactivate: VoidFunction;
  teamNamesById: Record<number, string>;
  teamTargets: Array<{ teamId: number; tenant: string; realmId: string }>;
}

export const DeactivateTeamsModal: FC<DeactivateTeamsModalProps> = ({
  onClose,
  onAfterDeactivate,
  teamNamesById,
  teamTargets,
}) => {
  const { deactivateTeams } = useDeactivateTeams();
  const [dataRetentionDays, setDataRetentionDays] = useState<number | undefined>();
  const [deactivationReason, setDeactivationReason] = useState<DeactivationReason | ''>('');

  const isConfirmDisabled =
    !deactivationReason ||
    (dataRetentionDays !== undefined &&
      (!Number.isInteger(dataRetentionDays) || dataRetentionDays <= 0));

  return (
    <SimpleModal
      open
      onClose={onClose}
      onConfirm={() => {
        if (!deactivationReason) {
          return;
        }
        deactivateTeams({
          dataRetentionDays,
          deactivationReason,
          teamTargets,
          onSuccess: () => {
            onAfterDeactivate?.();
          },
        });
        onClose();
      }}
      headerText="Teams Deactivation"
      confirmButtonText="Deactivate"
      onConfirmButtonClassNames="red-warning-button"
      disableConfirmButton={isConfirmDisabled}
      Body={
        <div>
          <div>
            You are about to deactivate <b>{teamTargets.length}</b>{' '}
            {teamTargets.length > 1 ? 'Teams' : 'Team'}.
          </div>
          <Box
            component="ul"
            sx={{
              maxHeight: 350,
              overflow: 'auto',
            }}
          >
            {teamTargets.map((t) => {
              const label = teamNamesById[t.teamId] ?? t.teamId;
              const key = `${t.teamId}-${t.tenant}-${t.realmId}`;
              return <li key={key}>{`${label} (${t.tenant}, ${t.realmId})`}</li>;
            })}
          </Box>
          <div>
            This action will revoke access to the accounts for all members effective immediately.
          </div>
          <Box
            sx={{
              mt: 2,
            }}
          >
            <DeactivationReasonSelect value={deactivationReason} onChange={setDeactivationReason} />
          </Box>
          <Box
            sx={{
              mt: 2,
            }}
          >
            <TextField
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      mr: 0.5,
                    }}
                  >
                    Custom Data Retention Period (days)
                  </Box>
                  <Tooltip
                    title={
                      'The time between "team get deactivated" and "team\'s data get hard-deleted"'
                    }
                  >
                    <InfoIcon style={{ fontSize: 16, color: '#6b7280' }} />
                  </Tooltip>
                </Box>
              }
              type="number"
              fullWidth
              value={dataRetentionDays ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setDataRetentionDays(value === '' ? undefined : Number(value));
              }}
              placeholder="Optional. Will use 180 days as default if not provided"
              slotProps={{
                htmlInput: { min: '1', step: '1' },
                inputLabel: { shrink: true },
              }}
            />
          </Box>
        </div>
      }
    />
  );
};

export default DeactivateTeamsModal;
